<template>
  <div class="notif-wrap" v-click-outside="closeDropdown">
    <!-- ปุ่มกระดิ่ง -->
    <button
      id="notif-bell-btn" type="button"
      class="notif-bell-btn"
      :class="{ active: isOpen }"
      :aria-label="$t('notification.bell')"
      aria-haspopup="true"
      :aria-expanded="isOpen.toString()"
      @click="toggleDropdown"
    >
      <!-- ไอคอนกระดิ่ง -->
      <svg class="notif-bell-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 2C10.3431 2 9 3.34315 9 5V5.26756C6.61284 6.20013 5 8.47706 5 11V16L3 18V19H21V18L19 16V11C19 8.47706 17.3872 6.20013 15 5.26756V5C15 3.34315 13.6569 2 12 2Z"
          stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
        />
        <path d="M10 19C10 20.1046 10.8954 21 12 21C13.1046 21 14 20.1046 14 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>

      <!-- Badge จำนวนที่ยังไม่อ่าน -->
      <span v-if="unreadCount > 0" class="notif-badge" aria-live="polite">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
    </button>

    <!-- Dropdown (Desktop) / Bottom Sheet (Mobile) -->
    <transition :name="isMobile ? 'slide-up' : 'fade-down'">
      <div
        v-if="isOpen"
        v-dialog-accessibility="{ modal: isMobile, close: closeDropdown }"
        class="notif-panel"
        :class="{ 'notif-panel--mobile': isMobile }"
        role="dialog"
        :aria-label="$t('notification.panelTitle')"
      >
        <!-- Header ของ Dropdown -->
        <div class="notif-panel__header">
          <div class="notif-panel__title-wrap">
            <h3 class="notif-panel__title">{{ $t('notification.panelTitle') }}</h3>
            <span v-if="notifications.length > 0" class="notif-panel__count">
              ({{ notifications.length }})
            </span>
          </div>

          <div class="notif-panel__actions">
            <!-- ปุ่มอ่านทั้งหมด -->
            <button
              id="notif-mark-all-btn"
              class="notif-btn notif-btn--mark-all"
              :disabled="unreadCount === 0"
              @click="handleMarkAll"
              :title="$t('notification.markAllRead')"
            >
              {{ $t('notification.markAllRead') }}
            </button>

            <!-- ปุ่มล้างที่อ่านแล้ว -->
            <button
              id="notif-clear-read-btn"
              class="notif-btn notif-btn--clear"
              :disabled="!hasReadNotifications"
              @click="handleClearRead"
              :title="$t('notification.clearRead')"
            >
              {{ $t('notification.clearRead') }}
            </button>
          </div>
        </div>

        <!-- กำลังโหลด -->
        <div v-if="loading" class="notif-panel__state">
          <div class="notif-spinner" role="status" :aria-label="$t('notification.loading')"></div>
        </div>

        <!-- ว่างเปล่า (ไม่มีแจ้งเตือน) -->
        <div v-else-if="notifications.length === 0" class="notif-panel__state">
          <svg class="notif-empty-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2C10.3431 2 9 3.34315 9 5V5.26756C6.61284 6.20013 5 8.47706 5 11V16L3 18V19H21V18L19 16V11C19 8.47706 17.3872 6.20013 15 5.26756V5C15 3.34315 13.6569 2 12 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
          <p class="notif-empty-text">{{ $t('notification.empty') }}</p>
        </div>

        <!-- รายการแจ้งเตือน (Scrollable List) -->
        <ul v-else class="notif-list" role="list">
          <li
            v-for="notif in notifications"
            :key="notif._id"
            class="notif-item"
            :class="{ 'notif-item--unread': !notif.isRead }"
            role="listitem"
            @click="handleItemClick(notif)"
          >
            <!-- จุดสีฟ้า = ยังไม่อ่าน -->
            <span class="notif-item__dot" :class="{ visible: !notif.isRead }" aria-hidden="true"></span>

            <div class="notif-item__body" v-keyboard-activate role="button" tabindex="0" @click.stop="handleItemClick(notif)">
              <span class="sr-only">{{ $t(notif.isRead ? 'notification.read' : 'notification.unread') }}</span>
              <p class="notif-item__title">
                {{ locale === 'en' ? notif.titleEn : notif.titleTh }}
              </p>
              <p v-if="notif.messageTh || notif.messageEn" class="notif-item__message">
                {{ locale === 'en' ? notif.messageEn : notif.messageTh }}
              </p>
              <span class="notif-item__time">{{ timeAgo(notif.createdAt) }}</span>
            </div>

            <!-- ส่วนขวาสุด: ไอคอนสถานะ / ปุ่มลบ -->
            <div class="notif-item__right">
              <span class="notif-item__icon" :class="`notif-item__icon--${notif.type}`" aria-hidden="true">
                {{ typeIcon(notif.type) }}
              </span>

              <button
                type="button" class="notif-item__del-btn keyboard-action"
                :title="$t('notification.delete')"
                :aria-label="$t('notification.delete')"
                @click="handleDeleteItem(notif._id, $event)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </li>
        </ul>
      </div>
    </transition>

    <!-- Backdrop สำหรับ Mobile (กด outside เพื่อปิด) -->
    <div
      v-if="isOpen && isMobile"
      class="notif-backdrop"
      role="presentation"
      @click="closeDropdown"
    ></div>

    <!-- Ticket Detail Modal: เปิดหน้ารายละเอียดเมื่อกดแจ้งเตือน (เหมือนกดปุ่ม "ติดตาม") -->
    <TicketDetailModal v-if="selectedTicket" @close="selectedTicket = null">
      <UReportDetail
        v-if="currentRole === 'user'"
        :report="selectedTicket"
        @close="selectedTicket = null"
        @refresh="handleModalRefresh"
      />
      <SupervisorReportDetail
        v-else-if="currentRole === 'supervisor'"
        :report="selectedTicket"
        :is-reporter="selectedTicket._role === 'reporter'"
        @close="selectedTicket = null"
        @refresh="handleModalRefresh"
      />
      <TechReportDetail
        v-else-if="currentRole === 'technician'"
        :report="selectedTicket"
        :is-reporter="false"
        @close="selectedTicket = null"
        @refresh="handleModalRefresh"
      />
      <AdminReportDetail
        v-else-if="currentRole === 'admin'"
        :report="selectedTicket"
        :is-reporter="false"
        @close="selectedTicket = null"
        @refresh="handleModalRefresh"
      />
    </TicketDetailModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import dialogAccessibility from '@/directives/dialogAccessibility';
import { mapGetters, mapActions } from "vuex";
import TicketDetailModal from "@/components/common/TicketDetailModal.vue";
import Service from "@/service/api";

export default {
  name: "NotificationDropdown",

  components: {
    TicketDetailModal,
    UReportDetail: () => import("@/pages/user/UReportDetail.vue"),
    SupervisorReportDetail: () => import("@/pages/supervisor/ReportDetail.vue"),
    TechReportDetail: () => import("@/pages/technician/ReportDetail.vue"),
    AdminReportDetail: () => import("@/pages/admin/ReportDetail.vue"),
  },

  directives: { dialogAccessibility, keyboardActivate,
    // v-click-outside: ปิด Dropdown เมื่อคลิกนอกพื้นที่
    "click-outside": {
      bind(el, binding) {
        el._clickOutsideHandler = (event) => {
          if (!el.contains(event.target)) {
            binding.value(event);
          }
        };
        document.addEventListener("click", el._clickOutsideHandler);
      },
      unbind(el) {
        document.removeEventListener("click", el._clickOutsideHandler);
      },
    },
  },

  data() {
    return {
      isOpen: false,
      windowWidth: window.innerWidth,
      selectedTicket: null,
      loadingTicket: false,
    };
  },

  computed: {
    ...mapGetters("notification", ["notifications", "unreadCount", "loading"]),
    ...mapGetters("locale", { locale: "lang" }),
    ...mapGetters("auth", ["role"]),

    /** Role ปัจจุบันของผู้ใช้ */
    currentRole() {
      return this.role || "user";
    },

    /** บนมือถือ (< 768px) ใช้ Bottom Sheet แทน Dropdown */
    isMobile() {
      return this.windowWidth < 768;
    },

    /** มีการแจ้งเตือนที่อ่านแล้วอย่างน้อย 1 รายการหรือไม่ */
    hasReadNotifications() {
      return this.notifications.some((n) => n.isRead);
    },
  },

  mounted() {
    window.addEventListener("resize", this.onResize);
    this.bindSocket();
    this.loadNotifications();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },

  methods: {
    ...mapActions("notification", [
      "loadNotifications",
      "bindSocket",
      "markOneAsRead",
      "markAllAsRead",
      "clearRead",
      "deleteOne",
    ]),

    onResize() {
      this.windowWidth = window.innerWidth;
    },

    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.loadNotifications();
      }
    },

    closeDropdown() {
      this.isOpen = false;
    },

    async handleItemClick(notif) {
      // 1. อ่านแจ้งเตือน (Optimistic)
      if (!notif.isRead) {
        await this.markOneAsRead(notif._id);
      }

      this.closeDropdown();

      // 2. ถ้ามี ticketId ให้ดึงข้อมูลและเปิดหน้าต่างเหมือนกด "ติดตาม"
      const ticketId = notif.ticket?._id || notif.ticket;
      if (ticketId) {
        this.loadingTicket = true;
        try {
          const res = await Service.ticket("get", null, { urlAppend: `/${ticketId}` });
          if (res?.data?.success && res.data.data) {
            this.selectedTicket = res.data.data;
            return;
          }
        } catch (err) {
          console.error("[Notification] Failed to load ticket detail for modal:", err);
        } finally {
          this.loadingTicket = false;
        }
      }

      // 3. Fallback หากไม่มี ticketId หรือโหลดไม่สำเร็จ ให้นำทางไป targetPath
      if (notif.targetPath && this.$route.path !== notif.targetPath) {
        this.$router.push(notif.targetPath);
      }
    },

    async handleModalRefresh() {
      const ticketId = this.selectedTicket?._id;
      if (!ticketId) return;
      try {
        const res = await Service.ticket("get", null, { urlAppend: `/${ticketId}` });
        if (res?.data?.success && res.data.data) {
          this.selectedTicket = res.data.data;
        }
      } catch (err) {
        console.error("[Notification] Failed to refresh ticket in modal:", err);
      }
    },

    async handleMarkAll() {
      await this.markAllAsRead();
    },

    async handleClearRead() {
      await this.clearRead();
    },

    async handleDeleteItem(id, event) {
      event.stopPropagation();
      await this.deleteOne(id);
    },

    /** แสดงเวลาแบบ "5 นาทีที่แล้ว" */
    timeAgo(dateStr) {
      if (!dateStr) return "";
      const now = Date.now();
      const diff = now - new Date(dateStr).getTime();
      const sec = Math.floor(diff / 1000);
      const min = Math.floor(sec / 60);
      const hr = Math.floor(min / 60);
      const day = Math.floor(hr / 24);

      const isEn = this.locale === "en";

      if (sec < 60)  return isEn ? "just now"           : "เมื่อสักครู่";
      if (min < 60)  return isEn ? `${min}m ago`        : `${min} นาทีที่แล้ว`;
      if (hr < 24)   return isEn ? `${hr}h ago`         : `${hr} ชั่วโมงที่แล้ว`;
      if (day === 1) return isEn ? "yesterday"           : "เมื่อวาน";
      return isEn ? `${day}d ago` : `${day} วันที่แล้ว`;
    },

    /** ไอคอน Emoji ตามประเภทแจ้งเตือน */
    typeIcon(type) {
      const icons = {
        ticket_created:     "📋",
        ticket_assigned:    "🔧",
        ticket_unassigned:  "❌",
        ticket_accepted:    "✅",
        ticket_finished:    "🎉",
        ticket_cancelled:   "🚫",
        ticket_eta_updated: "⏱️",
        ticket_forwarded:   "📨",
      };
      return icons[type] || "🔔";
    },
  },
};
</script>

<style scoped>
/* ==================== WRAPPER ==================== */
.notif-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

/* ==================== BELL BUTTON ==================== */
.notif-bell-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  color: #4b5563;
  transition: background 0.2s, color 0.2s, transform 0.2s;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
}
.notif-bell-btn:hover,
.notif-bell-btn.active {
  background: rgba(37, 99, 235, 0.08);
  color: #1d4ed8;
}
.notif-bell-btn:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
.notif-bell-icon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

/* ==================== UNREAD BADGE ==================== */
.notif-badge {
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  text-align: center;
  box-shadow: 0 0 0 2px #ffffff;
  box-sizing: border-box;
  pointer-events: none;
  user-select: none;
  font-family: "Sarabun", sans-serif;
}

/* ==================== PANEL (Desktop Dropdown) ==================== */
.notif-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 9999;
  width: 380px;
  max-height: min(480px, calc(100vh - 90px));
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 36px rgba(0, 0, 0, 0.13), 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ==================== PANEL HEADER ==================== */
.notif-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
  gap: 8px;
}
.notif-panel__title-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.notif-panel__title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}
.notif-panel__count {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.notif-panel__actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.notif-btn {
  font-size: 11.5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 7px;
  border-radius: 6px;
  font-weight: 600;
  transition: all 0.15s;
  white-space: nowrap;
}
.notif-btn--mark-all {
  color: #2563eb;
}
.notif-btn--mark-all:hover:not(:disabled) {
  background: rgba(37, 99, 235, 0.08);
}
.notif-btn--clear {
  color: #94a3b8;
}
.notif-btn--clear:hover:not(:disabled) {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
}
.notif-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  pointer-events: none;
}

/* ==================== EMPTY & LOADING STATES ==================== */
.notif-panel__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 44px 24px;
  text-align: center;
  color: #94a3b8;
}

.notif-empty-icon {
  width: 44px;
  height: 44px;
  color: #94a3b8;
  stroke: #94a3b8;
  margin-bottom: 12px;
  opacity: 0.55;
  flex-shrink: 0;
}

.notif-empty-text {
  font-size: 13.5px;
  color: #64748b;
  font-weight: 500;
  margin: 0;
}

.notif-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: notif-spin 0.8s linear infinite;
}

@keyframes notif-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ==================== NOTIFICATION LIST (Scrollable) ==================== */
.notif-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.notif-list::-webkit-scrollbar {
  width: 5px;
}
.notif-list::-webkit-scrollbar-track {
  background: transparent;
}
.notif-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}
.notif-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* ==================== NOTIFICATION ITEM ==================== */
.notif-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f8fafc;
  position: relative;
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-item:hover {
  background: #f8fafc;
}
.notif-item--unread {
  background: #eff6ff;
}
.notif-item--unread:hover {
  background: #dbeafe;
}

/* จุดสีฟ้า = ยังไม่อ่าน */
.notif-item__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3b82f6;
  flex-shrink: 0;
  margin-top: 6px;
  opacity: 0;
  transition: opacity 0.2s;
}
.notif-item__dot.visible { opacity: 1; }

.notif-item__body {
  flex: 1;
  min-width: 0;
}
.notif-item__title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 3px;
  line-height: 1.4;
  word-break: break-word;
}
.notif-item--unread .notif-item__title { color: #1d4ed8; }

.notif-item__message {
  font-size: 12px;
  color: #64748b;
  margin: 0 0 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.notif-item__time {
  font-size: 11px;
  color: #94a3b8;
}

/* ส่วนขวาสุด: ไอคอนสถานะ และปุ่มลบ (Overlay แทนที่กันเมื่อ hover) */
.notif-item__right {
  position: relative;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  margin-left: auto;
}

.notif-item__icon {
  font-size: 17px;
  line-height: 1;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.notif-item__del-btn {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.08);
  border: none;
  border-radius: 6px;
  color: #ef4444;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transform: scale(0.85);
  transition: opacity 0.15s ease, transform 0.15s ease, background 0.15s, color 0.15s;
}

.notif-item__del-btn svg {
  width: 14px;
  height: 14px;
}

.notif-item:hover .notif-item__icon {
  opacity: 0;
  transform: scale(0.8);
}

.notif-item:hover .notif-item__del-btn {
  opacity: 1;
  pointer-events: auto;
  transform: scale(1);
}

.notif-item__del-btn:hover {
  background: rgba(239, 68, 68, 0.18);
  color: #dc2626;
}

/* ==================== MOBILE: BOTTOM SHEET ==================== */
.notif-panel--mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: auto;
  width: 100%;
  max-width: 100%;
  max-height: 70vh;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.15);
  /* ยกขึ้นเหนือ Bottom Navigation (68px) */
  padding-bottom: env(safe-area-inset-bottom, 0px);
  margin-bottom: 68px;
}
.notif-panel--mobile .notif-panel__header {
  padding: 14px 20px 12px;
}
.notif-panel--mobile .notif-list {
  max-height: calc(70vh - 70px);
}

/* Backdrop สำหรับ Mobile */
.notif-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 9998;
  backdrop-filter: blur(2px);
}

/* ==================== ANIMATIONS ==================== */
/* Desktop: fade + slide down */
.fade-down-enter-active,
.fade-down-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-down-enter,
.fade-down-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Mobile: slide up from bottom */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.28s ease;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 767px) {
  /* ซ่อน Dropdown; ใช้ Bottom Sheet แทน */
  .notif-panel:not(.notif-panel--mobile) {
    display: none;
  }
  .notif-bell-btn {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 480px) {
  .notif-bell-icon {
    width: 20px;
    height: 20px;
  }
  .notif-badge {
    top: 2px;
    right: 2px;
    min-width: 16px;
    height: 16px;
    font-size: 9.5px;
  }
}

/* Tablet */
@media (min-width: 768px) and (max-width: 1024px) {
  .notif-panel {
    width: 320px;
    max-height: 420px;
  }
  /* ป้องกัน panel ล้นออกนอกจอทางซ้าย */
  .notif-panel {
    right: 0;
    left: auto;
  }
}
</style>
